import { httpFetchRiskFlaggedTransactions } from "api/admin/transaction";
import AdminSidebar from "components/layout/AdminSidebar";
import Container from "components/layout/Container";
import Main from "components/layout/Main";
import Pagination from "components/ui/Pagination";
import Table from "components/ui/Table";
import { Itransaction } from "components/ui/Transaction";
import { DateTime } from "luxon";
import { useCallback, useState } from "react";
import { useQuery } from "react-query";
import currency, { cryptoFormat, moneyFormat } from "utilities/currency";
import { capitalize } from "utilities/misc";
import TransactionInfo from "./TransactionInfo";

const limit = 50;

const transactionTypeLabel = (tr: Itransaction) => {
  switch (tr.transactionType) {
    case "withdrawal":
      return "Send Money";
    case "send":
      return `Send ${tr.crypto?.toUpperCase()}`;
    case "buy":
      return `Buy ${tr.crypto?.toUpperCase()}`;
    case "swap":
      return "Swap";
    default:
      return capitalize(tr.transactionType);
  }
};

const getDestination = (tr: Itransaction): string => {
  if (!tr.address || tr.address === "sell.bitoshi.africa") return "—";
  try {
    const addr = JSON.parse(tr.address);
    if (addr?.accountNumber && addr?.bankName) {
      return `${addr.accountNumber} (${addr.bankName})`;
    }
    if (typeof addr === "string") return addr;
    return tr.address;
  } catch {
    return tr.address || "—";
  }
};

const transactionAmount = (tr: Itransaction) =>
  tr?.currencyType === "naira" ||
  ["bill", "withdrawal", "refund"].includes(tr?.transactionType)
    ? `${currency("ngn")} ${moneyFormat(tr?.amount)}`
    : `${cryptoFormat(tr?.amount || 0)} ${tr?.crypto?.toUpperCase() || ""}`;

const RiskControlPanel: React.FC = () => {
  const [transaction, setTransaction] = useState<Itransaction | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: { transactions = [], total = 0, page = 1 } = {},
    refetch,
    isFetching,
    isError,
  } = useQuery(
    ["riskFlaggedTransactions", currentPage],
    () => httpFetchRiskFlaggedTransactions(limit, currentPage),
    {
      initialData: { transactions: [], total: 0, page: 1 },
    },
  );

  const handleClick = useCallback(
    (tr: Itransaction) => {
      if (transaction?.id === tr.id) {
        setTransaction(null);
        setIsOpen(false);
      } else {
        setTransaction(tr);
        setIsOpen(true);
      }
    },
    [transaction],
  );

  const handleClear = useCallback(() => {
    refetch().then(() => {
      setTransaction(null);
      setIsOpen(false);
    });
  }, [refetch]);

  return (
    <Container>
      <AdminSidebar />
      <Main className="mx-5 mt-5 text-left">
        <div className="flex items-center justify-between w-full">
          <h3 className="text-2xl">Risk Control - Pending Manual Review</h3>
        </div>
        <p className="mt-2 text-sm text-gray-600">
          Transactions flagged by risk rules. Review and approve or reject each
          withdrawal.
        </p>

        <div className="flex gap-8 mt-12">
          <div className="w-7/12">
            <Table
              isLoading={isFetching}
              loadingLabel="Loading transactions"
              emptyMessage="No transactions pending manual review."
              errorMessage={
                isError
                  ? "Unable to load transactions. Please try again."
                  : undefined
              }
              onRetry={() => {
                void refetch();
              }}
              header={[
                "Type",
                "User",
                "Amount",
                "Destination",
                "Reason",
                "Date",
              ]}
              data={transactions.map((tr: Itransaction) => [
                transactionTypeLabel(tr),
                `@${tr?.user?.userName || "—"}`,
                transactionAmount(tr),
                getDestination(tr),
                tr.riskFlagReason || "—",
                DateTime.fromISO(tr.timestamp).toFormat("DD t"),
                () => handleClick(tr),
              ])}
              selected={
                transaction
                  ? [
                      transactionTypeLabel(transaction),
                      `@${transaction?.user?.userName || "—"}`,
                      transactionAmount(transaction),
                      getDestination(transaction),
                      transaction.riskFlagReason || "—",
                      DateTime.fromISO(transaction.timestamp).toFormat("DD t"),
                      () => handleClick(transaction),
                    ]
                  : undefined
              }
            />
            <Pagination
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              limit={limit}
              page={page}
              total={total}
            />
          </div>
          {isOpen && transaction && (
            <TransactionInfo
              transaction={transaction}
              refetch={handleClear}
              full
            />
          )}
        </div>
      </Main>
    </Container>
  );
};

export default RiskControlPanel;
